import { Injectable } from '@angular/core';

/**
 * Module-level flag set once by the Polaris shell during `mount()` (see main.ts).
 *
 * The shell derives this value server-side from the `userProfileIsPolarisUx` feature flag
 * (AND the master `polarisEnableUxMode` toggle) and passes it to the MFE via `props.usePolarisUx`.
 * It is the single source of truth for whether the app should render in Polaris UX mode, so the
 * MFE and shell can never disagree (which previously caused the "multiple sidebar" bug).
 */
let SHELL_POLARIS_UX = false;

/**
 * Called from `main.ts#mount()` before Angular bootstraps, so `HostContextService.usePolarisUx`
 * can read the shell decision synchronously.
 */
export function setShellPolarisUx(value: boolean): void {
  SHELL_POLARIS_UX = value;
}

/**
 * Centralizes detection of the runtime host context.
 *
 * The app can run in two modes:
 *  - Polaris shell (single-spa MFE) — mounted under `/shippingplus/{locale}/profile/info/...`
 *    (L3) or the prefix-less `/{locale}/profile/info/...` (L6/prod).
 *  - Standalone dotcom — served at the root with hash routing
 *    (`/profile/{locale}/#/login`), so the route lives in the URL hash.
 *
 * Detection is intentionally URL-only: it is deterministic and stable for the
 * entire page lifecycle (the pathname is final at script load) and does not
 * collide with dotcom's own single-spa usage (the platform header/nav also
 * define `window.singleSpaNavigate`, so that global is NOT a safe Polaris signal).
 *
 * Keep all host-detection logic here so consumers (components, guards,
 * providers, etc.) share a single source of truth.
 */
@Injectable({
  providedIn: 'root'
})
export class HostContextService {

  /**
   * True when the app is running inside the Polaris shell.
   */
  get isPolaris(): boolean {
    const pathname = window.location.pathname;
    return pathname.includes('/shippingplus/') || pathname.includes('/profile/info');
  }

  /**
   * True when the app is running standalone (dotcom).
   */
  get isStandalone(): boolean {
    return !this.isPolaris;
  }

  /**
   * The shell's Polaris UX decision, passed from `props.usePolarisUx` during `mount()`
   * (see `setShellPolarisUx`). This is the single source of truth for whether the shell
   * renders the header + sidebar, so the MFE must not render its own when this is `true`.
   *
   * Only meaningful when running inside the Polaris shell (`isPolaris === true`); it stays
   * `false` in standalone/dotcom mode.
   */
  get usePolarisUx(): boolean {
    return SHELL_POLARIS_UX;
  }

  /**
   * Path prefix that precedes the locale segment inside the Polaris shell,
   * derived from the current URL so it stays correct across environments
   * without per-build configuration:
   *  - L3  (`/shippingplus/en-us/profile/info/login`) -> `/shippingplus`
   *  - L6  (`/en-us/profile/info/login`)              -> `` (empty)
   *
   * Used to build absolute links to sibling Polaris apps with the same
   * host/prefix convention as the currently running one.
   */
  get polarisPathPrefix(): string {
    const segments = window.location.pathname.split('/');
    const profileIndex = segments.indexOf('profile');
    // `profile` must exist and be preceded by at least a locale segment.
    if (profileIndex <= 0) {
      return '';
    }
    const localeIndex = profileIndex - 1;
    // Everything before the locale segment is the shell prefix (e.g. `/shippingplus`).
    return segments.slice(0, localeIndex).join('/');
  }
}
